import { useRef, useState } from "react";
import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { useLocation } from "wouter";

import { OPPORTUNITY_SUPPLIER_RELATIONSHIPS, useVariableValue } from "lib/featureFlags";
import { AiChatStreamResponse_Sources } from "lib/generated/app-service-gql/graphql";
import { useGetBidDocumentUrl } from "lib/hooks/api/bid/bid_document/useGetBidDocumentUrl";
import CentredSpinner from "../../lib/core_components/CentredSpinner";
import { useOpportunity } from "../../lib/hooks/api/opportunities/useOpportunity";
import { sysPrimaryDefault } from "../../lib/themes/colors";
import { DEFAULT_SUPPLIER_RELATIONSHIP_FILTER } from "../../pages/app/OpportunityPage";
import { Flex, Text } from "../../styles/utility-components";
import BetaIcon from "../ui/beta_icon/BetaIcon";
import { BlockNoteEditor, BlockNoteEditorRef } from "../ui/blocknote_editor/BlockNoteEditor";
import { isBlockNoteEmpty } from "../ui/blocknote_editor/utils";
import Button from "../ui/button/Button";
import EmptyState from "../ui/empty_state/EmptyState";
import Tabs, { TabItem } from "../ui/tabs/Tabs";
import AiAssistant from "./bid_writing_question_section/AiAssistant";
import { NOTICE_SOURCE } from "./qualification_report_section/DocumentSources";
import DocumentDrawer from "./DocumentViewer";
import { Scorecard_Source } from "./types";

interface BidWritingQuestionsPageProps {
  opportunityId: string;
}

export function BidWritingQuestionsPage({ opportunityId }: BidWritingQuestionsPageProps) {
  const theme = useTheme();
  const [, setLocation] = useLocation();
  const editorRef = useRef<BlockNoteEditorRef>(null);
  // TODO: This is a temporary change for the discovery calls
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isEditorEmpty, setIsEditorEmpty] = useState(true);
  const [isEditingQuestion, setIsEditingQuestion] = useState(false);

  const [documentData, setDocumentData] = useState<Scorecard_Source | null>(null);
  const [documentDrawerOpen, setDocumentDrawerOpen] = useState(false);

  // BID-417, please use this as the context
  const [questionText, setQuestionText] =
    useState(`Please explain the training and support approach for personnel, ensuring users across technical, administrative, and managerial roles understand and can securely operate the new cyber security system.
Include:
• Onboarding and refresher training methods (in-person, digital, self-serve)
• Role-specific content for technical vs. non-technical users
• Ongoing support, helpline/ticketing system availability
• Support for remote workers and third-party vendors`);

  const isSupplierRelationshipsEnabled = useVariableValue(
    OPPORTUNITY_SUPPLIER_RELATIONSHIPS,
    false,
  );

  // TODO: Combine this with the one in AskStotlesAiModal when we place the
  // bid writing questions page in the correct place in the app
  const { mutateAsync: getBidDocumentUrlMutation } = useGetBidDocumentUrl();

  const {
    data: opportunity,
    isLoading,
    isError,
  } = useOpportunity(
    opportunityId,
    isSupplierRelationshipsEnabled ? DEFAULT_SUPPLIER_RELATIONSHIP_FILTER : undefined,
  );

  // TODO: Implement the notice link handling logic when this page is align with bid data
  const handleNoticeLink = () => {};

  const handleDocumentLink = async (source: AiChatStreamResponse_Sources) => {
    if (!source.documentId) {
      return;
    }

    const { getBidDocumentUrl } = await getBidDocumentUrlMutation({ id: source.documentId });
    const documentUrl = getBidDocumentUrl.documentUrl;

    setDocumentDrawerOpen(true);
    setDocumentData({
      ...source,
      documentLink: documentUrl,
    });
  };

  const onSelectDocument = async (source: AiChatStreamResponse_Sources) => {
    if (source?.documentId === NOTICE_SOURCE) {
      handleNoticeLink();
    } else {
      await handleDocumentLink(source);
    }
  };

  if (isLoading) {
    return <CentredSpinner />;
  }

  if (isError) {
    return (
      <Flex flexGrow={1} alignItems="center">
        <EmptyState
          type="loadError"
          heading="Sorry something has gone wrong loading this response"
          description="If this error persists please contact support"
          width="100%"
          size="large"
        />
      </Flex>
    );
  }

  const tabItems: TabItem[] = [
    {
      key: "ai-assistant",
      label: "AI Assistant",
      icon: "magicWand",
      children: (
        <AiAssistant
          resourceId={opportunityId}
          resourceName={opportunity?.name}
          bidQuestion={questionText}
          onSelectDocument={onSelectDocument}
        />
      ),
    },
    {
      key: "comments",
      label: "Comments",
      icon: "messageTextSquare",
      children: <div />,
    },
    {
      key: "details",
      label: "Details",
      icon: "infoCircle",
      children: <div />,
    },
  ];

  return (
    <>
      <Container>
        <BreadcrumbContainer>
          <Flex alignItems="center" gap={8}>
            <BreadcrumbButton
              color={sysPrimaryDefault}
              onClick={() => {
                if (
                  history.state &&
                  history.state.from === "/" &&
                  history.state.previousSearchParams
                ) {
                  return setLocation(`/?${history.state.previousSearchParams}`);
                }
                setLocation("/");
              }}
            >
              Opportunities
            </BreadcrumbButton>
            <Text color={theme.colors.sysTextSecondary}> / </Text>
            <BreadcrumbButton
              color={sysPrimaryDefault}
              onClick={() => {
                if (
                  history.state &&
                  history.state.from === "/" &&
                  history.state.previousSearchParams
                ) {
                  return setLocation(`/?${history.state.previousSearchParams}`);
                }
                setLocation(`/${opportunityId}`);
              }}
            >
              {opportunity?.name}
            </BreadcrumbButton>
            <Text color={theme.colors.sysTextSecondary}> / </Text>
            <BreadcrumbButton
              color={sysPrimaryDefault}
              onClick={() => {
                if (
                  history.state &&
                  history.state.from === "/" &&
                  history.state.previousSearchParams
                ) {
                  return setLocation(`/?${history.state.previousSearchParams}`);
                }
                setLocation(`/${opportunityId}/bid_writing_questions`);
              }}
            >
              Responses
            </BreadcrumbButton>
            <Text color={theme.colors.sysTextSecondary}> / </Text>
            <Text color={theme.colors.sysTextDefault}>Lorem ipsum dolor sit amet</Text>
            <BetaIcon />
          </Flex>
          <Flex gap={8}>
            <Button icon="dotsHorizontal" buttonType="primary" buttonStyle="inverse" />
            <Button icon="share07">Share</Button>
            {/* <Button icon="messageTextSquare" buttonType="primary" buttonStyle="inverse" />
            <Button icon="magicWand" buttonType="primary" buttonStyle="inverse" /> */}
          </Flex>
        </BreadcrumbContainer>
        <Flex height="100%">
          <Content>
            <Flex justifyContent="space-between" height="100%">
              <MainContent>
                <QuestionDropdown>
                  <Text bold>Training strategy & user support</Text>
                  <div onClick={() => setIsEditingQuestion(true)}>
                    {isEditingQuestion ? (
                      <QuestionTextArea
                        value={questionText}
                        onChange={(e) => setQuestionText(e.target.value)}
                        onBlur={() => setIsEditingQuestion(false)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && e.ctrlKey) {
                            setIsEditingQuestion(false);
                          }
                        }}
                        autoFocus
                      />
                    ) : (
                      <QuestionText>{questionText}</QuestionText>
                    )}
                  </div>
                  <Flex alignItems="center" gap={24}>
                    <Text>Word count: 1000 words</Text>
                    <Text>Weighting: 5%</Text>
                    <Button icon="fileSearch02" buttonType="tertiary">
                      Appendices 3-4_Spec...
                    </Button>
                  </Flex>
                </QuestionDropdown>
                <BlockNoteEditor
                  ref={editorRef}
                  theme="light"
                  minHeight="0"
                  staticFormattingToolbar
                  onChange={(content) => {
                    setIsEditorEmpty(isBlockNoteEmpty(content));
                  }}
                />

                {/* {isEditorEmpty && (
                  <Flex flexDirection="column" gap={16}>
                    <Text>Get started with</Text>
                    <Flex alignItems="center" gap={8} flexWrap="wrap">
                      <Button icon="edit04" buttonType="secondary">
                        Draft a storyboard
                      </Button>
                      <Button icon="folderSearch" buttonType="secondary">
                        Source relevant content
                      </Button>
                      <Button icon="magicWand" buttonType="secondary">
                        Generate a response
                      </Button>
                      <Button icon="ruler" buttonType="secondary">
                        Score my response
                      </Button>
                    </Flex>
                  </Flex>
                )} */}
              </MainContent>
              <Sidebar>
                <Tabs items={tabItems} defaultValue={tabItems[0].key} fullWidth />
              </Sidebar>
            </Flex>
          </Content>
        </Flex>
      </Container>
      {documentData && (
        <DocumentDrawer
          open={documentDrawerOpen}
          source={documentData}
          onClose={() => setDocumentDrawerOpen(false)}
        />
      )}
    </>
  );
}

const Container = styled(Flex)({
  flexDirection: "column",
  flexGrow: 1,
  width: 480,
  height: "100vh",
  overflow: "hidden",
});

const BreadcrumbContainer = styled(Flex)(({ theme }) => ({
  justifyContent: "space-between",
  alignItems: "center",
  padding: "16px 24px",
  borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
}));

const BreadcrumbButton = styled.button<{ color?: string }>(({ color }) => ({
  all: "unset",
  cursor: "pointer",
  padding: 0,
  color: color ?? "inherit",
}));

const Content = styled(Flex)({
  flexDirection: "column",
  flexGrow: 1,
  width: "100%",
  height: "calc(100vh - 48px)", // Subtract breadcrumb height
  overflow: "hidden",
});

const MainContent = styled(Flex)({
  flexDirection: "column",
  flexGrow: 1,
  gap: 24,
  width: "100%",
  height: "100%",
  padding: 24,
  overflow: "auto",
});

const QuestionDropdown = styled(Flex)(({ theme }) => ({
  flexDirection: "column",
  gap: 16,
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  borderRadius: 8,
  padding: 16,
}));

const Sidebar = styled(Flex)(({ theme }) => ({
  width: 560,
  flexShrink: 0,
  borderLeft: `1px solid ${theme.colors.sysBorderDecorative}`,
  height: "100%",
  flexDirection: "column",
  overflow: "hidden",
}));

const QuestionTextArea = styled.textarea(({ theme }) => ({
  fontSize: "14px",
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  borderRadius: "4px",
  padding: "8px",
  width: "100%",
  minHeight: "150px",
  resize: "vertical",
  fontFamily: "inherit",
  outline: "none",
  whiteSpace: "pre-wrap",
  "&:focus": {
    borderColor: theme.colors.sysPrimaryDefault,
  },
}));

const QuestionText = styled.p({
  cursor: "pointer",
  margin: 0,
  whiteSpace: "pre-wrap",
  "&:hover": {
    opacity: 0.8,
  },
});
